import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ListMusic,
  User,
  Music,
  Copy,
  Play,
  Pause,
  Loader2,
  ArrowLeft,
  Plus,
  Volume2,
  Zap,
  Moon,
  Flame,
  Coffee,
  Target
} from "lucide-react";
import { toast } from "sonner";

const vibeIconMap = {
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
  Music
};

export default function SharedPlaylistPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();
  const [playlist, setPlaylist] = useState(null);
  const [vibes, setVibes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [addingSong, setAddingSong] = useState(null);
  const [playingItem, setPlayingItem] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [playlistRes, vibesRes] = await Promise.all([
        axios.get(`${API}/playlists/shared/${userId}`),
        axios.get(`${API}/vibes`)
      ]);
      setPlaylist(playlistRes.data);
      setVibes(vibesRes.data.vibes || []);
    } catch (error) {
      console.error("Failed to fetch playlist", error);
      toast.error("Playlist not found");
      navigate("/discover");
    } finally {
      setLoading(false);
    }
  };

  const getVibeInfo = (vibeId) => vibes.find(v => v.id === vibeId);

  const handleCopyPlaylist = async () => {
    if (!user) {
      toast.info("Please sign in to copy playlists");
      navigate("/login");
      return;
    }

    setCopying(true);
    try {
      await axios.post(`${API}/playlist/copy`, { source_user_id: userId });
      toast.success(`Copied ${playlist.owner_name}'s playlist to your library!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy playlist");
    } finally {
      setCopying(false);
    }
  };

  const handleAddSong = async (contentId) => {
    if (!user) {
      toast.info("Please sign in to add songs");
      navigate("/login");
      return;
    }

    setAddingSong(contentId);
    try {
      await axios.post(`${API}/playlist/copy-song`, { content_id: contentId });
      toast.success("Added to your playlist!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add song");
    } finally {
      setAddingSong(null);
    }
  };

  const handlePlay = async (item) => {
    if (playingItem?.content_id === item.content_id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    if (playingItem?.content_id === item.content_id && !isPlaying) {
      audioRef.current?.play();
      setIsPlaying(true);
      return;
    }

    try {
      const response = await axios.get(`${API}/content/stream/${item.content_id}`);
      setPlayingItem({ ...item, streamUrl: response.data.stream_url });
      setIsPlaying(true);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.info("Please sign in to play content");
        navigate("/login");
      } else {
        toast.error("Failed to play content");
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!playlist) {
    return null;
  }

  return (
    <div className="min-h-screen py-8" data-testid="shared-playlist-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate("/discover")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Discover
        </Button>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* Owner Info */}
              <div className="flex items-center gap-4">
                {playlist.owner_picture ? (
                  <img 
                    src={playlist.owner_picture} 
                    alt={playlist.owner_name}
                    className="w-20 h-20 rounded-full"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center">
                    <User className="h-10 w-10 text-gold" />
                  </div>
                )}
                <div>
                  <h1 className="font-serif text-2xl font-bold">{playlist.owner_name}'s Playlist</h1>
                  <p className="text-muted-foreground">
                    {playlist.total} songs • Shared with community
                  </p>
                  {playlist.description && (
                    <p className="text-sm text-muted-foreground mt-1 italic">
                      "{playlist.description}"
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 md:ml-auto">
                <Button
                  className="bg-gold text-primary-foreground hover:bg-gold/90"
                  onClick={handleCopyPlaylist}
                  disabled={copying}
                >
                  {copying ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Copy All Songs
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Songs List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif flex items-center gap-2">
              <ListMusic className="h-5 w-5 text-gold" />
              Songs ({playlist.total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {playlist.items.map((item, index) => {
                const vibe = getVibeInfo(item.vibe);
                const VibeIcon = vibe ? vibeIconMap[vibe.icon] : Music;
                const isCurrentlyPlaying = playingItem?.content_id === item.content_id;

                return (
                  <div
                    key={item.content_id}
                    className={`flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors ${
                      isCurrentlyPlaying ? 'bg-gold/10' : ''
                    }`}
                    data-testid={`shared-song-${item.content_id}`}
                  >
                    {/* Index */}
                    <div className="w-8 text-center">
                      {isCurrentlyPlaying && isPlaying ? (
                        <Volume2 className="h-4 w-4 text-gold mx-auto animate-pulse" />
                      ) : (
                        <span className="text-sm text-muted-foreground">{index + 1}</span>
                      )}
                    </div>

                    {/* Vibe Icon */}
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${vibe?.color || '#c9a227'}20` }}
                    >
                      <VibeIcon className="h-5 w-5" style={{ color: vibe?.color || '#c9a227' }} />
                    </div>

                    {/* Song Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium truncate ${isCurrentlyPlaying ? 'text-gold' : ''}`}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {item.course_name} • {vibe?.name || item.vibe}
                      </p>
                    </div>

                    {/* Duration */}
                    <span className="text-sm text-muted-foreground">
                      {formatDuration(item.duration)}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddSong(item.content_id)}
                        disabled={addingSong === item.content_id}
                        title="Add to my playlist"
                      >
                        {addingSong === item.content_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant={isCurrentlyPlaying ? "default" : "ghost"}
                        className={isCurrentlyPlaying ? "bg-gold text-primary-foreground" : ""}
                        onClick={() => handlePlay(item)}
                      >
                        {isCurrentlyPlaying && isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audio Player */}
      {playingItem && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 z-50">
          <div className="container mx-auto max-w-4xl flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm truncate">{playingItem.title}</h4>
              <p className="text-xs text-muted-foreground">{playingItem.course_name}</p>
            </div>
            <audio
              ref={audioRef}
              src={playingItem.streamUrl}
              autoPlay
              controls
              className="w-64"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
